import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { selectUserInfo, logout } from "@redux/slices/authSlice";
import logo from "../assets/images/logo2.png";
import { askAgent, getBISessions, getBIHistory, deleteBISession, transcribeVoice, getAgentSessions, getAgentHistory, deleteAgentSession } from "../services/httpservice";
import ScrollContainer from "@components/misc/ScrollContainer";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ModalConfirm from "@components/misc/ModalConfirm";
import {
  PhaseCard,
  PhaseTimeline,
  ConnectionStatusCard,
  MappingCard,
  InputFormCard,
  IntegrationSummaryCard,
  FlowDiagram,
  AgentMessageCard,
  QueryPreviewCard,
  MetricCard,
  DataTableCard,
  QueryPlanCard,
  ModelReasoningCard,
  DiagramEditModal,
} from "../components/agent";
import ConnectionSelectionCard from "../components/agent/ConnectionSelectionCard";
import D2IntegrationDiagram from "@components/agent/D2IntegrationDiagram";

// ─── Paginated Markdown Table ───
// Used by mdComponents.table so every ReactMarkdown-rendered table has 5-row pagination.
const PaginatedMarkdownTable = ({ node, children, ...props }) => {
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 5;

  // ReactMarkdown passes rendered <thead> and <tbody> elements as children.
  const childArray = React.Children.toArray(children);
  const tbodyIdx = childArray.findIndex(
    (c) =>
      React.isValidElement(c) &&
      (c.type === "tbody" || c.props?.node?.tagName === "tbody" || c.type?.name === "tbody")
  );

  // No tbody found — render plain table.
  if (tbodyIdx === -1) {
    return (
      <div className="overflow-x-auto my-3 w-fit max-w-full rounded-xl border border-blue-200 shadow-sm">
        <table className="text-sm border-collapse" {...props}>{children}</table>
      </div>
    );
  }

  const tbody = childArray[tbodyIdx];
  // Filter out newline strings/text nodes that ReactMarkdown might insert
  const rows = React.Children.toArray(tbody.props.children).filter(
    (c) => React.isValidElement(c)
  );
  const totalPages = Math.ceil(rows.length / PAGE_SIZE);
  const currentPage = Math.min(page, Math.max(0, totalPages - 1));
  const pageRows = rows.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

  const paginatedTbody = React.cloneElement(tbody, {}, ...pageRows);
  const newChildren = [
    ...childArray.slice(0, tbodyIdx),
    paginatedTbody,
    ...childArray.slice(tbodyIdx + 1),
  ];

  return (
    <div className="custom-markdown-table-wrapper my-3 w-fit max-w-full rounded-xl border border-blue-200 shadow-sm overflow-hidden bg-white">
      <div className="overflow-x-auto">
        <table className="text-sm border-collapse w-full" {...props}>{newChildren}</table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white border-t border-blue-100">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={currentPage === 0}
            className="px-3 py-1 flex items-center justify-center rounded-lg border border-blue-200 bg-white text-[0.8rem] text-blue-600 font-bold disabled:opacity-40 hover:bg-blue-100 active:scale-95 transition"
          >
            Prev
          </button>
          <span className="text-[0.72rem] text-blue-500 font-semibold min-w-[80px] text-center">
            {`${currentPage * PAGE_SIZE + 1}–${Math.min((currentPage + 1) * PAGE_SIZE, rows.length)} of ${rows.length}`}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={currentPage >= totalPages - 1}
            className="px-3 py-1 flex items-center justify-center rounded-lg border border-blue-200 bg-white text-[0.8rem] text-blue-600 font-bold disabled:opacity-40 hover:bg-blue-100 active:scale-95 transition"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Phase Badge Component ───
const PhaseBadge = ({ phase }) => {
  const phaseConfig = {
    discover: { label: "Step 1 · Discover", color: "bg-blue-50 text-blue-600 border-blue-200" },
    connect: { label: "Step 2 · Connect", color: "bg-amber-50 text-amber-600 border-amber-200" },
    map: { label: "Step 3 · Map", color: "bg-purple-50 text-purple-600 border-purple-200" },
    build: { label: "Step 4 · Build", color: "bg-green-50 text-green-600 border-green-200" },
    done: { label: "✅ Complete", color: "bg-emerald-50 text-emerald-600 border-emerald-200" },
  };
  const config = phaseConfig[phase] || phaseConfig.discover;

  return (
    <span className={`inline-flex items-center text-[0.7rem] font-semibold px-2.5 py-1 rounded-full border ${config.color}`}>
      {config.label}
    </span>
  );
};

// ─── Step Progress Timeline ───
const StepTimeline = ({ steps }) => {
  if (!steps || steps.length === 0) return null;

  return (
    <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
      {steps.map((step, i) => (
        <div key={i} className="flex items-center gap-1">
          {step.status === "done" ? (
            <svg className="w-3.5 h-3.5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="w-3.5 h-3.5 text-amber-400 animate-spin" style={{ animationDuration: '2s' }} fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
          )}
          <span className={`text-[0.72rem] ${step.status === "done" ? "text-gray-500" : "text-amber-600 font-medium"}`}>
            {step.step}
          </span>
          {i < steps.length - 1 && <span className="text-gray-300 mx-0.5">→</span>}
        </div>
      ))}
    </div>
  );
};

// ─── Agent Badge — shows which agent handled a response ───
const AgentBadge = ({ agentUsed }) => {
  if (!agentUsed || agentUsed === "router") return null;
  const cfg = {
    builder: { label: "Builder", icon: "🛠", cls: "bg-blue-50 text-blue-600 border-blue-200" },
    analyst: { label: "Data Analyst", icon: "📊", cls: "bg-violet-50 text-violet-600 border-violet-200" },
  };
  const c = cfg[agentUsed];
  if (!c) return null;
  return (
    <span className={`inline-flex items-center gap-1 text-[0.65rem] font-bold px-2 py-0.5 rounded-full border ${c.cls} mb-1.5`}>
      <span>{c.icon}</span>{c.label}
    </span>
  );
};

// ─── Clarification Chips — rendered when router can't decide intent ───
const ClarificationChips = ({ question, options, onSelect }) => (
  <div className="mt-2 space-y-2">
    <p className="text-[0.82rem] text-black font-medium">{question}</p>
    <div className="flex flex-wrap gap-2">
      {(options || []).map((opt, i) => (
        <button
          key={i}
          onClick={() => onSelect(opt)}
          className="px-3.5 py-1.5 text-[0.78rem] font-semibold rounded-full border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
        >
          {opt}
        </button>
      ))}
    </div>
  </div>
);

// ─── Sparkle Icon (replaces Agent Box) ───
const SparkleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.5 2C11.693 6.94 15.06 10.307 20 10.5C15.06 10.693 11.693 14.06 11.5 19C11.307 14.06 7.94 10.693 3 10.5C7.94 10.307 11.307 6.94 11.5 2Z" />
    <path d="M19.5 14C19.6 15.6 20.8 16.8 22.4 16.9C20.8 17 19.6 18.2 19.5 19.8C19.4 18.2 18.2 17 16.6 16.9C18.2 16.8 19.4 15.6 19.5 14Z" />
  </svg>
);

// ─── Collapsible Thinking Block Component ───
const FILLER_PREFIXES = [
  /^(let me|i need to|i should|i will|i'll|i must|i have to|first[,\s]|now[,\s]|next[,\s]|ok[,\s]|okay[,\s]|alright[,\s]|so[,\s]|well[,\s]|actually[,\s]|basically[,\s])/i,
  /^(looking at|thinking about|considering|checking|reading|reviewing|analyzing this)/i,
];

function summarizeThinking(raw) {
  if (!raw) return [];
  const lines = raw
    .split(/\n+/)
    .map(l => l.replace(/^[-•*\d.]+\s*/, '').trim())
    .filter(l => l.length > 15);

  // Keep lines that are conclusions/findings, drop meta-cognitive filler
  const meaningful = lines.filter(l => !FILLER_PREFIXES.some(rx => rx.test(l)));

  // Deduplicate near-identical lines (same first 40 chars)
  const seen = new Set();
  const deduped = meaningful.filter(l => {
    const key = l.slice(0, 40).toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // Cap at 5 bullets; truncate each to 120 chars
  return deduped.slice(0, 5).map(l => l.length > 120 ? l.slice(0, 117) + '…' : l);
}

const ThinkingBlock = ({ thinking }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!thinking) return null;

  const bullets = summarizeThinking(thinking);
  if (bullets.length === 0) return null;

  return (
    <div className="mb-3">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors group"
      >
        <svg className="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2L14.09 8.26L20 9.27L15.55 13.97L16.91 20L12 16.9L7.09 20L8.45 13.97L4 9.27L9.91 8.26L12 2Z" />
        </svg>
        <span>Reasoning</span>
        <svg
          className={`w-3 h-3 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-[500px] opacity-100 mt-2' : 'max-h-0 opacity-0'}`}
      >
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-[0.82rem] text-amber-900 leading-relaxed shadow-sm">
          <ul className="space-y-1.5">
            {bullets.map((bullet, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-amber-400 mt-[2px] shrink-0">•</span>
                <span>{bullet}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

// ─── Update Options Picker ────────────────────────────────────────────────────
// Shown when the agent lists integrations / pipelines for the user to pick to update.
// Clicking a card = sends the option name as a chat message automatically.
const UpdateOptionsPicker = ({ options, onSelect, disabled }) => {
  if (!options || options.length === 0) return null;

  return (
    <div className="mt-3 flex flex-col gap-2 w-full max-w-2xl my-1">
      <p className="text-[0.72rem] font-black text-gray-400 tracking-widest mb-1 flex items-center gap-1.5">
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15.232 5.232l3.536 3.536M9 13l-4 1 1-4L17.5 4a2.121 2.121 0 013 3L9 13z" />
        </svg>
        Select to Update
      </p>
      {options.map((opt, i) => {
        const isPipeline = !!opt.is_pipeline;
        const displayName = opt.name.replace(/ \(Pipeline\)$/, "");
        return (
          <button
            key={opt.id || i}
            disabled={disabled}
            onClick={() => onSelect(opt.name)}
            className={`group flex items-center gap-3 w-full px-4 py-2 rounded-xl border text-left transition-all duration-200
              ${disabled
                ? "border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed"
                : isPipeline
                  ? "border-purple-200 bg-purple-50 hover:bg-purple-100 hover:border-purple-400 hover:shadow-md cursor-pointer active:scale-[0.98]"
                  : "border-blue-100 bg-blue-50 hover:bg-blue-100 hover:border-blue-400 hover:shadow-md cursor-pointer active:scale-[0.98]"
              }`}
          >
            {/* Icon badge */}
            <span className={`shrink-0 flex items-center justify-center w-9 h-9 rounded-lg text-lg font-bold
              ${isPipeline ? "bg-purple-200 text-purple-700" : "bg-blue-200 text-blue-700"}`}>
              {isPipeline ? "⇄" : "→"}
            </span>

            {/* Name + type label */}
            <span className="flex-1 min-w-0">
              <span className={`block text-[0.88rem] font-semibold leading-tight truncate
                ${isPipeline ? "text-purple-900" : "text-blue-900"}`}>
                {displayName}
              </span>
              <span className={`inline-block mt-0.5 text-[0.65rem] font-black uppercase tracking-widest
                ${isPipeline ? "text-purple-400" : "text-blue-400"}`}>
                {isPipeline ? "Pipeline" : "Integration"}
              </span>
            </span>

            {/* Chevron */}
            {!disabled && (
              <svg className={`w-4 h-4 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity
                ${isPipeline ? "text-purple-500" : "text-blue-500"}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
              </svg>
            )}
          </button>
        );
      })}
    </div>
  );
};

// ─── Collapsible Text Wrapper (hides text behind toggle when cards are shown) ───
const CollapsibleText = ({ children, autoExpand = false }) => {
  const [isExpanded, setIsExpanded] = useState(autoExpand);

  return (
    <div className="mt-2 ml-2">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-1.5 text-[0.72rem] font-semibold text-gray-400 hover:text-gray-600 transition-colors"
      >
        <svg
          className={`w-3 h-3 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
        <span>{isExpanded ? "Collapse text" : "Show text"}</span>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-[2000px] opacity-100 mt-2' : 'max-h-0 opacity-0'}`}
      >
        {children}
      </div>
    </div>
  );
};

// ─── Phase-aware Loading Animation ───
const ThinkingLoader = ({ phase }) => {
  const [dots, setDots] = useState("");

  const phaseSteps = {
    discover: [
      "Understanding your request",
      "Identifying trigger and destination platforms",
      "Extracting integration requirements",
    ],
    connect: [
      "Checking saved connections",
      "Identifying required credentials",
      "Preparing credential setup",
    ],
    map: [
      "Analyzing source and destination schemas",
      "Auto-generating field mappings",
      "Preparing mapping for your review",
    ],
    build: [
      "Generating integration workflow",
      "Building configuration payload",
      "Validating integration setup",
    ],
  };

  const steps = phaseSteps[phase] || phaseSteps.discover;
  const [visibleSteps, setVisibleSteps] = useState(0);

  useEffect(() => {
    const dotsInterval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? "" : prev + ".");
    }, 400);
    return () => clearInterval(dotsInterval);
  }, []);

  useEffect(() => {
    if (visibleSteps < steps.length) {
      const timer = setTimeout(() => {
        setVisibleSteps(prev => prev + 1);
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, [visibleSteps, steps.length]);

  const phaseLabel = {
    discover: "Discovering",
    connect: "Connecting",
    map: "Mapping fields",
    build: "Building",
  };

  return (
    <div className="flex w-full justify-start gap-3 py-2">
      {/* Agent icon — matches agent message layout */}
      <div className="shrink-0 mt-1">
        <SparkleIcon className="w-5 h-5 text-blue-600 animate-pulse" />
      </div>

      <div className="flex flex-col tracking-[0.015em] leading-[1.7]">
        <div className="text-[1.1rem] text-gray-800 font-bold tracking-tight">
          {phaseLabel[phase] || "Thinking"}{dots}
        </div>

        {/* Animated steps */}
        <div className="space-y-1 mt-2">
          {steps.slice(0, visibleSteps).map((step, i) => (
            <div
              key={i}
              className="flex items-center gap-2 text-[0.95rem] text-gray-600 opacity-80 animate-fadeIn"
              style={{ animation: 'fadeIn 0.4s ease-in-out' }}
            >
              <span className="text-[#2563eb]">•</span>
              <span>{step}</span>
              {i === visibleSteps - 1 && (
                <span className="flex space-x-1 ml-1">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></span>
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></span>
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></span>
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── BI Agent Thinking Loader — floating dots only ───
const BIThinkingLoader = () => (
  <div className="flex w-full justify-start gap-3 py-2">
    <div className="shrink-0 mt-1">
      <SparkleIcon className="w-5 h-5 text-blue-500 animate-pulse" />
    </div>
    <div className="flex items-center gap-1.5 mt-1.5">
      <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
      <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
      <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
    </div>
  </div>
);

const AgentChat = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const userInfo = useSelector(selectUserInfo);
  const [inputText, setInputText] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [currentPhase, setCurrentPhase] = useState("discover");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sessionIdToDelete, setSessionIdToDelete] = useState(null);
  const [, setStepsCompleted] = useState([]);
  const [isListening, setIsListening] = useState(false);
  const [recentChats, setRecentChats] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [activeView, setActiveView] = useState("chat"); // "chat" | "chats_list"
  // Tracks which agent handled the most recent turn — used for session reload logic.
  // Not a user-controlled mode; the Router LangGraph decides automatically.
  const [agentUsedLast, setAgentUsedLast] = useState(null); // "builder" | "analyst" | null

  // ── DiagramEditModal state ──────────────────────────────────────────────────
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editModalData, setEditModalData] = useState({
    integrationId: null,
    currentFlow: {},
    currentMappings: [],
    currentBranchMappings: null, // per-branch for pipeline (fan-out/fan-in); null = use currentMappings
    flowGraph: null,
  });
  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const silenceTimerRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const vadFrameRef = useRef(null);
  const speechDetectedRef = useRef(false);
  const recognitionRef = useRef(null);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  // ── Helper: human-readable relative time ──────────────────────────────
  const timeAgo = (dateStr) => {
    if (!dateStr) return "";
    const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} minute${Math.floor(diff / 60) === 1 ? "" : "s"} ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hour${Math.floor(diff / 3600) === 1 ? "" : "s"} ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} day${Math.floor(diff / 86400) === 1 ? "" : "s"} ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  // Fetch all sessions (builder + BI) merged and sorted newest-first.
  // The sidebar now shows every conversation regardless of which agent handled it.
  const fetchSessions = async (_unused) => {
    try {
      const [builderRes, biRes] = await Promise.allSettled([
        getAgentSessions(),
        getBISessions(),
      ]);
      const builderSessions = (builderRes.status === "fulfilled" && builderRes.value)
        ? (Array.isArray(builderRes.value) ? builderRes.value : (builderRes.value.data || []))
        : [];
      const biSessions = (biRes.status === "fulfilled" && biRes.value)
        ? (Array.isArray(biRes.value) ? biRes.value : (biRes.value.data || []))
        : [];

      // Tag each session so delete knows which endpoint to call
      const tagged = [
        ...builderSessions.map(s => ({ ...s, _sessionType: "builder" })),
        ...biSessions.map(s => ({ ...s, _sessionType: "analyst" })),
      ].sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
        .slice(0, 8);

      setRecentChats(tagged);
    } catch (error) {
      console.error("Failed to fetch agent sessions:", error);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  // Auto-load a specific session when navigating from the integration details page
  // via /agent?session=<session_id>
  useEffect(() => {
    const sessionParam = searchParams.get("session");
    if (sessionParam) {
      loadHistory(sessionParam);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced backend search — fires 350ms after the user stops typing.
  // When query is empty the sidebar resets to the 20 most recent sessions.
  useEffect(() => {
    if (!searchQuery.trim()) {
      fetchSessions();
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const [builderRes, biRes] = await Promise.allSettled([
          getAgentSessions(searchQuery.trim()),
          getBISessions(searchQuery.trim()),
        ]);
        const builderSessions = (builderRes.status === "fulfilled" && builderRes.value)
          ? (Array.isArray(builderRes.value) ? builderRes.value : (builderRes.value.data || []))
          : [];
        const biSessions = (biRes.status === "fulfilled" && biRes.value)
          ? (Array.isArray(biRes.value) ? biRes.value : (biRes.value.data || []))
          : [];
        const merged = [
          ...builderSessions.map(s => ({ ...s, _sessionType: "builder" })),
          ...biSessions.map(s => ({ ...s, _sessionType: "analyst" })),
        ].sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
          .slice(0, 8);
        setRecentChats(merged);
      } catch (error) {
        console.error("Failed to search sessions:", error);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadHistory = async (sessionId) => {
    try {
      setIsLoading(false); // cancel any in-progress AI request state
      setIsHistoryLoading(true);
      setActiveView("chat"); // switch to chat view when opening a session
      // Determine history endpoint from the _sessionType tag on the sidebar entry.
      // Fall back to trying BI first (then builder) if the tag is absent —
      // this handles page-load via ?session= URL param when recentChats is empty.
      const sessionMeta = recentChats.find(s => s.session_id === sessionId);
      let isBISession = sessionMeta?._sessionType === "analyst";

      const is404 = (err) => err?.response?.status === 404;

      let response;
      if (sessionMeta) {
        // Sidebar entry is available — use the tagged endpoint directly.
        const historyFn = isBISession ? getBIHistory : getAgentHistory;
        try {
          response = await historyFn(sessionId);
        } catch (err) {
          if (is404(err)) {
            // New session with no history yet — treat as empty
            setMessages([]);
            setCurrentSessionId(sessionId);
            return;
          }
          throw err;
        }
      } else {
        // No sidebar entry (e.g. URL param on page load) — try BI first, then builder.
        try {
          response = await getBIHistory(sessionId);
          isBISession = true;
        } catch (err) {
          if (is404(err)) {
            try {
              response = await getAgentHistory(sessionId);
              isBISession = false;
            } catch (err2) {
              if (is404(err2)) {
                setMessages([]);
                setCurrentSessionId(sessionId);
                return;
              }
              throw err2;
            }
          } else {
            throw err;
          }
        }
      }

      setAgentUsedLast(isBISession ? "analyst" : "builder");
      // Builder history API returns: { messages, step, step_label, progress, is_active, title }
      const data = response && response.data ? response.data : response;
      if (data && data.messages && data.messages.length > 0) {
        const historyMessages = data.messages.map((msg, idx) => {
          const base = {
            role: msg.is_user ? "user" : "agent",
            content: msg.message,
            phase: data.step || "discover",
            agent_used: isBISession ? "analyst" : undefined,
          };
          if (!msg.is_user) {
            // Use per-message metadata if saved (new messages), otherwise fall back
            // to session-level card data for the last agent message only.
            const meta = msg.message_metadata;
            const isLastAgentMsg = idx === data.messages.length - 1;
            if (meta) {
              base.phase_cards = meta.phase_cards || null;
              base.structured_data = meta.structured_data || null;
              base.needs_input = false;       // don't re-show input forms on history
              base.input_fields = [];
              base.input_prompt = "";
              base.input_platform = "";
              base.is_done = meta.is_done || meta.step === "done" || meta.phase === "done" || (isLastAgentMsg && data.step === "done") || false;
              base.reasoning_steps = meta.reasoning_steps || [];
              base.pipeline_type = meta.pipeline_type || null;
              base.flow_graph = meta.flow_graph || null;
              base.pipeline_id = meta.pipeline_id || null;
              base.integration_id = meta.integration_id || null;
            } else if (isLastAgentMsg) {
              // Legacy fallback (no metadata saved): only attach to last message
              base.phase_cards = data.phase_cards || null;
              base.structured_data = data.structured_data || null;
              base.needs_input = false;
              base.input_fields = [];
              base.input_prompt = "";
              base.input_platform = "";
              base.is_done = data.step === "done";
            }
            // Older messages without metadata render as plain AgentMessageCard text
          }
          return base;
        });
        setMessages(historyMessages);
        setCurrentSessionId(sessionId);
        // Restore current step from session state
        if (data.step) {
          setCurrentPhase(data.step);
        }
      } else {
        setMessages([]);
        setCurrentSessionId(sessionId);
      }
    } catch (error) {
      console.error("Failed to load chat history:", error);
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const isPartialMatch = (query, target) => {
    if (!query.trim()) return true;

    const queryStr = query.toLowerCase();
    const targetStr = target.toLowerCase();

    // 1. Direct inclusion
    if (targetStr.includes(queryStr)) return true;

    // 2. Word by word match (at least 50% of words must be present)
    const words = queryStr.split(/\s+/).filter(Boolean);
    if (words.length > 0) {
      const matchedWords = words.filter(word => targetStr.includes(word));
      if (matchedWords.length / words.length >= 0.5) {
        return true;
      }
    }

    // 3. Character sequence match (at least 50% of characters must match in order)
    let qIndex = 0;
    let matchCount = 0;
    for (let i = 0; i < targetStr.length; i++) {
      if (qIndex < queryStr.length && targetStr[i] === queryStr[qIndex]) {
        matchCount++;
        qIndex++;
      }
    }

    if (queryStr.length > 0 && matchCount / queryStr.length >= 0.5) {
      return true;
    }

    return false;
  };

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isLoading]);

  const bottomInputRef = useRef(null);
  const centerInputRef = useRef(null);

  // Focus input when switching states
  useEffect(() => {
    if (messages.length > 0 && bottomInputRef.current) {
      bottomInputRef.current.focus();
    } else if (messages.length === 0 && centerInputRef.current) {
      centerInputRef.current.focus();
    }
  }, [messages.length]);

  const handleTextChange = (e) => {
    setInputText(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = `${e.target.scrollHeight}px`;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // ── Helper: process a final response from either streaming or non-streaming ──
  const _applyAgentResponse = (response) => {
    if (response.session_id && !currentSessionId) {
      setCurrentSessionId(response.session_id);
    }
    if (response.step) setCurrentPhase(response.step);
    else if (response.phase) setCurrentPhase(response.phase);
    if (response.steps_completed?.length > 0) setStepsCompleted(response.steps_completed);

    setMessages(prev => [...prev, {
      role: "agent",
      content: response.response || "No response received.",
      thinking: response.thinking || "",
      phase: response.step || response.phase || currentPhase,
      steps_completed: response.steps_completed || [],
      phase_cards: response.phase_cards || null,
      structured_data: response.structured_data || null,
      needs_input: response.needs_input || false,
      input_fields: response.input_fields || [],
      input_prompt: response.input_prompt || "",
      input_platform: response.input_platform || "",
      needs_selection: response.needs_selection || false,
      selection_options: response.selection_options || [],
      selection_platform: response.selection_platform || "",
      integration_id: response.integration_id || response.pipeline_id || null,
      pipeline_id: response.pipeline_id || null,
      pipeline_type: response.pipeline_type || null,
      flow_graph: response.flow_graph || null,
      is_done: response.is_done || response.step === "done" || response.phase === "done" || false,
      update_options: response.update_options || [],
      awaiting_update: response.awaiting_update || false,
      // Router clarification fields
      agent_used: response.agent_used || null,
      needs_clarification: response.needs_clarification || false,
      clarification_options: response.clarification_options || [],
      // BI Agent approval flow
      needs_approval: response.needs_approval || false,
      // BI Agent model reasoning trace
      reasoning_steps: response.reasoning_steps || [],
    }]);
    fetchSessions();
  };

  // ── Send message ──
  const handleSendMessage = async () => {
    if (!inputText.trim() || isLoading) return;
    const userMessage = inputText.trim();
    setInputText("");

    // If there's no session yet the router will decide — show neutral dots loader
    if (!currentSessionId) setAgentUsedLast(null);

    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      const raw = await askAgent(userMessage, currentSessionId);
      const response = raw.data || raw;
      // Track which agent handled this turn (for session resume + badge)
      if (response.agent_used) setAgentUsedLast(response.agent_used);
      _applyAgentResponse(response);
    } catch (error) {
      console.error("Agent error:", error);
      setMessages(prev => [...prev, { role: "agent", content: "Sorry, I encountered an error while communicating with the agent." }]);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Clarification chip selected — re-send with force_route ──
  const handleClarificationSelect = async (option) => {
    if (isLoading) return;
    // Map chip label to route ("Build an integration" → "builder", anything else → "analyst")
    const forceRoute = option.toLowerCase().includes("build") ? "builder" : "analyst";
    // Find the last user message to re-send
    const lastUserMsg = [...messages].reverse().find(m => m.role === "user")?.content || "";
    if (!lastUserMsg) return;
    setIsLoading(true);
    setMessages(prev => [...prev, { role: "user", content: option, isAction: true }]);
    try {
      const raw = await askAgent(lastUserMsg, currentSessionId, false, false, forceRoute);
      const response = raw.data || raw;
      if (response.agent_used) setAgentUsedLast(response.agent_used);
      _applyAgentResponse(response);
    } catch (error) {
      console.error("Clarification error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Query approval / cancel — routed directly to BI agent via approve flag ──
  const handleApproveQuery = async () => {
    if (isLoading) return;
    setIsLoading(true);
    setMessages(prev => [...prev, { role: "user", content: "[Running query...]", isAction: true }]);
    try {
      const raw = await askAgent("", currentSessionId, true, false);
      const response = raw.data || raw;
      _applyAgentResponse(response);
    } catch (error) {
      console.error("Query approve error:", error);
      setMessages(prev => [...prev, { role: "agent", content: "Sorry, I encountered an error executing the query." }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelQuery = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const raw = await askAgent("", currentSessionId, false, true);
      const response = raw.data || raw;
      setMessages(prev => [...prev, { role: "user", content: "[Query cancelled]", isAction: true }]);
      _applyAgentResponse(response);
    } catch (error) {
      console.error("Query cancel error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setCurrentPhase("discover");
    setStepsCompleted([]);
    setInputText("");
    setIsListening(false);
    setCurrentSessionId(null);
    setAgentUsedLast(null); // reset so router decides loader for next first message
    setActiveView("chat"); // always switch to chat view
  };

  const handleDeleteSession = async (sessionId, e) => {
    e.stopPropagation(); // prevent triggering loadHistory
    if (!window.confirm("Delete this conversation? This cannot be undone.")) return;
    try {
      // Use the _sessionType tag we set during fetchSessions merge
      const sessionMeta = recentChats.find(s => s.session_id === sessionId);
      const deleteFn = sessionMeta?._sessionType === "analyst" ? deleteBISession : deleteAgentSession;
      await deleteFn(sessionId);
      // If the deleted session is currently open, clear the chat area
      if (currentSessionId === sessionId) {
        handleNewChat();
      }
      // Refresh sidebar
      fetchSessions();
    } catch (error) {
      console.error("Failed to delete session:", error);
    }
  };

  // handleModeSwitch removed — mode is now auto-detected by the Router LangGraph.

  // Voice recording: uses browser SpeechRecognition for real-time text (near-zero latency)
  // Falls back to MediaRecorder + Whisper for unsupported browsers (Firefox)
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

  const stopRecording = () => {
    setIsListening(false);
    if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch { } recognitionRef.current = null; }
    if (vadFrameRef.current) { cancelAnimationFrame(vadFrameRef.current); vadFrameRef.current = null; }
    if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; }
    if (audioContextRef.current) { audioContextRef.current.close().catch(() => { }); audioContextRef.current = null; }
    analyserRef.current = null;
    speechDetectedRef.current = false;
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
  };

  const handleVoiceToggle = async () => {
    if (isListening) {
      stopRecording();
      return;
    }

    // ── Path A: Browser SpeechRecognition (Chrome, Edge, Safari) ──
    if (SpeechRecognition) {
      try {
        const recognition = new SpeechRecognition();
        recognition.lang = "en-US";
        recognition.interimResults = true;
        recognition.continuous = false;
        recognition.maxAlternatives = 1;

        recognition.onresult = (event) => {
          let transcript = "";
          for (let i = 0; i < event.results.length; i++) {
            transcript += event.results[i][0].transcript;
          }
          setInputText(transcript);
          // Auto-resize textarea
          setTimeout(() => {
            const ta = bottomInputRef.current || centerInputRef.current;
            if (ta) { ta.style.height = "auto"; ta.style.height = `${ta.scrollHeight}px`; }
          }, 0);
        };

        recognition.onend = () => {
          setIsListening(false);
          recognitionRef.current = null;
          setTimeout(() => {
            const ta = bottomInputRef.current || centerInputRef.current;
            if (ta) ta.focus();
          }, 0);
        };

        recognition.onerror = (event) => {
          console.error("SpeechRecognition error:", event.error);
          setIsListening(false);
          recognitionRef.current = null;
        };

        recognitionRef.current = recognition;
        recognition.start();
        setIsListening(true);
      } catch (err) {
        console.error("SpeechRecognition failed:", err);
        setIsListening(false);
      }
      return;
    }

    // ── Path B: Fallback — MediaRecorder + Whisper (Firefox, etc.) ──
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
          sampleRate: 48000,
        },
      });

      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 128000,
      });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      speechDetectedRef.current = false;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        if (audioBlob.size === 0) return;

        try {
          const tRaw = await transcribeVoice(audioBlob);
          const transcript = (tRaw.data || tRaw).transcript || "";
          setInputText(transcript);
          setTimeout(() => {
            const ta = bottomInputRef.current || centerInputRef.current;
            if (ta) { ta.style.height = "auto"; ta.style.height = `${ta.scrollHeight}px`; ta.focus(); }
          }, 0);
        } catch {
          // Silent fail — user can type manually
        }
      };

      // Silence detection via Web Audio API
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.3;
      source.connect(analyser);
      analyserRef.current = analyser;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const SILENCE_THRESHOLD = 15;
      const SILENCE_DURATION = 1200;
      const SPEECH_THRESHOLD = 20;

      const monitorAudio = () => {
        if (!analyserRef.current) return;
        analyser.getByteFrequencyData(dataArray);
        const rms = Math.sqrt(dataArray.reduce((sum, v) => sum + v * v, 0) / dataArray.length);

        if (rms > SPEECH_THRESHOLD) {
          speechDetectedRef.current = true;
          if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; }
        } else if (speechDetectedRef.current && rms < SILENCE_THRESHOLD) {
          if (!silenceTimerRef.current) {
            silenceTimerRef.current = setTimeout(() => {
              stopRecording();
            }, SILENCE_DURATION);
          }
        }

        vadFrameRef.current = requestAnimationFrame(monitorAudio);
      };

      mediaRecorder.start();
      setIsListening(true);
      vadFrameRef.current = requestAnimationFrame(monitorAudio);
    } catch (err) {
      console.error("Microphone access denied:", err);
      setIsListening(false);
    }
  };

  // ── Normalize one mapping object regardless of which field names the backend used ──
  const normalizeMapping = (m) => ({
    source_label: m.source_label || m.source_field || m.source || m.from || m.input || "",
    destination_label: m.destination_label || m.dest_field || m.dest || m.to || m.output || "",
    transformation: m.transformation || m.transform || "",
  });

  // Extract mappings + flowGraph + legMappings from any structured_data shape
  const extractFromStructuredData = (sd) => {
    const sdData = sd?.data || {};

    // 1:1 flat field_mappings
    const rawMappings =
      sdData.field_mappings ||
      sdData.mappings ||
      sd?.field_mappings ||
      [];
    const arr = Array.isArray(rawMappings)
      ? rawMappings
      : Object.entries(rawMappings).map(([k, v]) => ({
        source_label: k,
        destination_label: typeof v === "string" ? v : v?.destination || "",
      }));

    // Per-branch mappings for pipeline (fan-out / fan-in)
    // Backend sends: leg_mappings: [ [{source_label,destination_label,...}], [{...}] ]
    const rawLegMappings = sdData.leg_mappings || sd?.leg_mappings || null;
    const legMappings = Array.isArray(rawLegMappings)
      ? rawLegMappings.map((branch) =>
        (Array.isArray(branch) ? branch : []).map(normalizeMapping)
      )
      : null;

    return {
      mappings: arr.map(normalizeMapping),
      legMappings,  // null for 1:1, array-of-arrays for pipeline
      flowGraph: sdData.flow_graph || null,
    };
  };

  // ── Open DiagramEditModal — fetches live session state so old sessions work too ──
  const openDiagramEdit = async (msg) => {
    const sd = msg.structured_data || {};
    const sdData = sd.data || {};

    // Initial extract from the stored message
    let { mappings, legMappings, flowGraph } = extractFromStructuredData(sd);

    // For sequential integrations, flow_graph lives at the message level (msg.flow_graph),
    // not inside structured_data.data.flow_graph — fall back to it when needed.
    // Also fall back if structured_data's flow_graph has nodes but all labels are empty
    // (happens when _build_flow_graph ran before sequential support was added).
    const hasValidLabels = flowGraph?.nodes?.some((n) => n.label && n.label.trim() !== "");
    if ((!flowGraph || !hasValidLabels) && msg.flow_graph) {
      flowGraph = msg.flow_graph;
    }

    // Normalize sequential node types: backend emits "connector" for non-trigger nodes.
    // DiagramEditModal needs the last node to be "destination" and middle nodes to be "source".
    if (flowGraph?.nodes?.length > 0 && flowGraph.nodes.some((n) => n.type === "connector")) {
      flowGraph = {
        ...flowGraph,
        nodes: flowGraph.nodes.map((n, i, arr) => {
          if (n.type === "connector") {
            return { ...n, type: i === arr.length - 1 ? "destination" : "source" };
          }
          return n;
        }),
      };
    }

    // If both flat mappings AND leg mappings are empty (old session without field_mappings in DB),
    // fetch the LIVE session state — getAgentHistory calls extract_structured_data()
    // which always includes field_mappings / leg_mappings from the current state.
    const hasAnyMappings = mappings.length > 0 ||
      (Array.isArray(legMappings) && legMappings.some((b) => b.length > 0));

    if (!hasAnyMappings && currentSessionId) {
      try {
        const raw = await getAgentHistory(currentSessionId);
        const liveData = raw?.data || raw || {};
        const liveSd = liveData.structured_data || {};
        const extracted = extractFromStructuredData(liveSd);
        if (extracted.mappings.length > 0) mappings = extracted.mappings;
        if (extracted.legMappings) legMappings = extracted.legMappings;
        if (extracted.flowGraph) flowGraph = extracted.flowGraph;
      } catch (e) {
        console.warn("[EditModal] Could not fetch live session state:", e);
      }
    }

    // Derive platforms from flowGraph nodes
    const nodes = flowGraph?.nodes || [];
    const trigger = nodes.find((n) => n.type === "trigger")?.label || sdData.trigger_platform || "";
    const source = nodes.find((n) => n.type === "source")?.label || "";
    const destination = nodes.find((n) => n.type === "destination")?.label || sdData.destination_platform || "";

    console.log("[EditModal] data — flowGraph:", flowGraph, "mappings:", mappings, "legMappings:", legMappings);

    setEditModalData({
      integrationId: msg.integration_id || msg.pipeline_id || sdData.integration_id || null,
      currentFlow: { trigger, source, destination },
      currentMappings: mappings,
      currentBranchMappings: legMappings,  // null for 1:1, array-of-branch-arrays for pipeline
      flowGraph,
    });
    setIsEditModalOpen(true);
  };

  // ── Handle modal Apply — build a simple plain-text update message ──
  const handleDiagramEdit = ({ nodeChanges, changedBranchMappings, updatedNodes }) => {
    const lines = [];

    // Platform changes — identify branch if it's a source/destination
    if (nodeChanges && nodeChanges.length > 0) {
      nodeChanges.forEach((nc) => {
        if (nc.type === "destination" || nc.type === "source") {
          lines.push(`Change Leg ${nc.branchIdx >= 0 ? nc.branchIdx + 1 : 1} ${nc.type} from ${nc.from || "empty"} to ${nc.to}.`);
        } else {
          lines.push(`Change ${nc.type} from ${nc.from || "empty"} to ${nc.to}.`);
        }
      });
    }

    // Mapping changes — list per branch explicitly categorized
    if (changedBranchMappings && changedBranchMappings.length > 0) {
      changedBranchMappings.forEach((branchMaps, bIdx) => {
        const { added, modified, removed } = branchMaps || {};

        if (added?.length > 0 || modified?.length > 0 || removed?.length > 0) {
          lines.push(`Update field mappings for Leg ${bIdx + 1}:`);
          if (added?.length > 0) {
            lines.push(`  Added:`);
            added.forEach((m) => lines.push(`    - ${m.source_label} mapped to ${m.destination_label}`));
          }
          if (modified?.length > 0) {
            lines.push(`  Modified:`);
            modified.forEach((m) => lines.push(`    - ${m.source_label} changed mapping from ${m.old_destination_label} to ${m.destination_label}`));
          }
          if (removed?.length > 0) {
            lines.push(`  Removed:`);
            removed.forEach((m) => lines.push(`    - ${m.source_label} mapping removed`));
          }
        }
      });
    }

    if (lines.length === 0) return;

    const message = `Please update this integration:\n${lines.join("\n")}`;
    const { integrationId } = editModalData;

    // Show in chat as a user message
    setMessages((prev) => [...prev, { role: "user", content: message }]);
    setIsLoading(true);

    // Send via existing askAgent — backend loads existing integration via integration_id
    askAgent(message, currentSessionId, false, false, "", integrationId)
      .then((raw) => {
        const resp = raw.data || raw;
        if (resp.agent_used) setAgentUsedLast(resp.agent_used);
        _applyAgentResponse(resp);
      })
      .catch(() =>
        setMessages((prev) => [
          ...prev,
          { role: "agent", content: "Sorry, an error occurred while updating the integration." },
        ])
      )
      .finally(() => setIsLoading(false));
  };

  // ── CTA action: sends a proceed message (shows as action chip, not user bubble) ──
  const handleCTAAction = (text, asChip = true) => {
    if (!text || isLoading) return;
    setMessages(prev => [...prev, { role: "user", content: text, isAction: asChip }]);
    setIsLoading(true);

    askAgent(text, currentSessionId)
      .then(raw => {
        const resp = raw.data || raw;
        if (resp.agent_used) setAgentUsedLast(resp.agent_used);
        _applyAgentResponse(resp);
      })
      .catch(() => setMessages(prev => [...prev, { role: "agent", content: "Sorry, an error occurred." }]))
      .finally(() => setIsLoading(false));
  };

  return (
    <div className="flex h-full w-full bg-white font-sans text-gray-900 overflow-hidden">
      {/* Inline keyframe animation for fadeIn */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.4s ease-in-out; }
        
        @keyframes soundWave {
          0%, 100% { transform: scaleY(1); }
          50% { transform: scaleY(1.8); }
        }
        @keyframes micPulseRing {
          0% { transform: scale(1); opacity: 0.5; }
          100% { transform: scale(2.2); opacity: 0; }
        }
        @keyframes micGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.5); }
          50% { box-shadow: 0 0 12px 4px rgba(59, 130, 246, 0.35); }
        }
        @keyframes micBreathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.12); }
        }
        .mic-listening { animation: micGlow 1.5s ease-in-out infinite, micBreathe 1.5s ease-in-out infinite; }
        .mic-pulse-ring { position: absolute; inset: 0; border-radius: 0.75rem; border: 2px solid #3b82f6; animation: micPulseRing 1.5s ease-out infinite; pointer-events: none; }

        .sound-wave-active .sound-wave-1 { animation: soundWave 0.5s ease-in-out infinite; animation-delay: 0.1s; transform-origin: center; fill: #2563eb; }
        .sound-wave-active .sound-wave-2 { animation: soundWave 0.6s ease-in-out infinite; animation-delay: 0.3s; transform-origin: center; fill: #2563eb; }
        .sound-wave-active .sound-wave-3 { animation: soundWave 0.7s ease-in-out infinite; animation-delay: 0.0s; transform-origin: center; fill: #2563eb; }
        .sound-wave-active .sound-wave-4 { animation: soundWave 0.5s ease-in-out infinite; animation-delay: 0.2s; transform-origin: center; fill: #2563eb; }

        .group:hover .sound-wave-1:not(.sound-wave-active *) { animation: soundWave 0.8s ease-in-out infinite; animation-delay: 0.1s; transform-origin: center; }
        .group:hover .sound-wave-2:not(.sound-wave-active *) { animation: soundWave 0.8s ease-in-out infinite; animation-delay: 0.3s; transform-origin: center; }
        .group:hover .sound-wave-3:not(.sound-wave-active *) { animation: soundWave 0.8s ease-in-out infinite; animation-delay: 0.0s; transform-origin: center; }
        .group:hover .sound-wave-4:not(.sound-wave-active *) { animation: soundWave 0.8s ease-in-out infinite; animation-delay: 0.2s; transform-origin: center; }

        /* Dynamically highlight any paragraph immediately preceding a table */
        .markdown-body p:has(+ .custom-markdown-table-wrapper) {
          font-weight: 600;
          color: #000000;
          font-size: 1.1rem;
          margin-bottom: 0.6rem;
          margin-top: 1.5rem;
          text-transform: capitalize;
          letter-spacing: 0.015em;
          line-height: 1.2;
        }
      `}</style>

      {/* Sidebar - Expanded State */}
      {isSidebarOpen ? (
        <div className="w-[300px] flex-shrink-0 border-r border-gray-200 bg-white flex flex-col z-20 shadow-sm relative">
          <div className="p-3 space-y-2 mt-2">
            <div className="flex items-center gap-2">
              <button
                onClick={handleNewChat}
                className="flex items-center gap-3 flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg transition-all rounded-xl font-bold text-[0.95rem] active:scale-95"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                New chat
              </button>
              <button
                onClick={() => setIsSidebarOpen(false)}
                className="flex items-center justify-center w-10 h-10 rounded-xl border border-gray-200 text-gray-400 hover:text-blue-600 hover:border-blue-300 bg-white transition text-3xl font-bold flex-shrink-0"
                style={{ lineHeight: 1, paddingBottom: "6px" }}
                title="Collapse sidebar"
              >
                «
              </button>
            </div>

            {isSearching ? (
              <div className="flex items-center gap-3 w-full px-4 py-2.5 bg-gray-100 border border-blue-200 rounded-xl">
                <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                <input
                  autoFocus
                  type="text"
                  placeholder="Search chats..."
                  className="bg-transparent outline-none text-[0.95rem] text-gray-900 placeholder-gray-400 w-full font-medium"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button onClick={() => { setIsSearching(false); setSearchQuery(""); }} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsSearching(true)}
                className="flex items-center gap-3 w-full px-4 py-2.5 text-[0.95rem] rounded-xl transition text-left text-gray-700 font-bold hover:bg-gray-100"
              >
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                Search
              </button>
            )}

            <button
              onClick={() => { setActiveView("chats_list"); fetchSessions(); setSearchQuery(""); }}
              className={`flex items-center gap-3 w-full px-4 py-2.5 text-[0.95rem] rounded-xl transition text-left font-bold ${activeView === "chats_list"
                ? "text-blue-700 bg-blue-50 border border-blue-100"
                : "text-gray-700 hover:bg-gray-100"
                }`}
            >
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
              Chats
            </button>
          </div>

          {/* <Phase Progress removed as per user request> */}

          {/* Recents */}
          <div className="mt-4 px-3 flex-1 overflow-y-auto custom-scrollbar">
            <h3 className="px-3 text-[0.7rem] font-bold text-gray-400 uppercase tracking-widest mb-3">
              {searchQuery.trim() ? "Search Results" : "Recent Chats"}
            </h3>
            <ul className="space-y-0.5">
              {recentChats.length > 0 ? (
                recentChats.map(chat => (
                  <li
                    key={chat.session_id}
                    onClick={() => loadHistory(chat.session_id)}
                    className={`group flex items-center justify-between px-3 py-2.5 text-[0.9rem] rounded-xl cursor-pointer transition font-bold ${currentSessionId === chat.session_id
                      ? 'bg-blue-50 text-blue-700 border border-blue-100 shadow-sm'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                  >
                    <span className="truncate flex-1 min-w-0">
                      {chat.title || `Chat ${chat.session_id?.slice(0, 8)}`}
                    </span>
                    {/* Trash icon — only visible on hover */}
                    <button
                      onClick={(e) => handleDeleteSession(chat.session_id, e)}
                      className="flex-shrink-0 ml-1.5 p-1 rounded opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-400 hover:bg-red-400/10 transition"
                      title="Delete conversation"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </li>
                ))
              ) : (
                <li className="px-3 py-2 text-[0.9rem] text-gray-400 font-medium italic">
                  {searchQuery.trim() ? "No matching chats found" : "No recent chats"}
                </li>
              )}
            </ul>
          </div>

          <div className="mt-auto" />
        </div>
      ) : (
        /* Sidebar - Closed State (Icon Only) */
        <div className="w-[68px] flex-shrink-0 border-r border-gray-200 bg-white flex flex-col hidden md:flex items-center py-4 relative z-20">
          <button onClick={() => setIsSidebarOpen(true)} className="p-2 mb-6 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-gray-100 transition" title="Expand Sidebar">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h7" /></svg>
          </button>

          <button onClick={handleNewChat} className="p-2.5 mb-4 text-white bg-blue-600 rounded-xl hover:bg-blue-700 shadow-md transition active:scale-95" title="New Chat">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
          </button>

          <button onClick={() => { setIsSidebarOpen(true); setIsSearching(true); }} className="p-2.5 mb-2 text-gray-400 rounded-xl hover:text-gray-600 hover:bg-gray-100 transition" title="Search">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          </button>

          <button onClick={() => { setIsSidebarOpen(true); setActiveView("chats_list"); fetchSessions(); }} className="p-2.5 text-gray-400 rounded-xl hover:text-gray-600 hover:bg-gray-100 transition" title="Chats">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
          </button>

          <div className="mt-auto" />
        </div>
      )}

      {/* Main Area */}
      <div className="flex-1 flex flex-col relative h-full bg-transparent overflow-hidden">

        {/* ── Chats List Panel ───────────────────────────────────────────── */}
        {activeView === "chats_list" ? (
          <div className="flex-1 overflow-y-auto bg-white text-gray-900 flex justify-center">
            <div className="w-full max-max-2xl px-6 pt-10">
              {/* Header */}
              <div className="flex items-center justify-between pb-5">
                <h1 className="text-2xl font-extrabold bg-gradient-to-r from-blue-700 via-purple-700 to-indigo-800 bg-clip-text text-transparent uppercase tracking-wide">Chats</h1>
                <button
                  onClick={handleNewChat}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-lg transition shadow-md active:scale-95"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" /></svg>
                  New chat
                </button>
              </div>

              {/* Search bar */}
              <div className="pb-4">
                <div className="flex items-center gap-3 border border-gray-200 rounded-xl px-4 py-2.5 bg-gray-50 focus-within:border-blue-400 focus-within:bg-white transition-all">
                  <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                  <input
                    autoFocus
                    type="text"
                    placeholder="Search your chats..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1 bg-transparent outline-none text-[0.9rem] text-gray-900 placeholder-gray-400 font-medium"
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery("")} className="text-gray-400 hover:text-gray-600">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Subtitle */}
              <div className="pb-3 text-xs text-gray-500 uppercase tracking-wide">
                Your chats with Agent
              </div>

              {/* Chat list */}
              <ul className="space-y-1">
                {recentChats.length > 0 ? (
                  recentChats.map(chat => (
                    <li
                      key={chat.session_id}
                      onClick={() => loadHistory(chat.session_id)}
                      className={`group flex items-center justify-between px-3 py-3 rounded-xl cursor-pointer transition border ${currentSessionId === chat.session_id
                        ? "bg-blue-50 border-blue-200 shadow-sm"
                        : "border-transparent hover:bg-blue-50/50 hover:border-blue-100"
                        }`}
                    >
                      {/* Blue left accent bar for active chat */}
                      <div className={`w-1 self-stretch rounded-full mr-3 flex-shrink-0 ${currentSessionId === chat.session_id ? "bg-blue-500" : "bg-transparent"
                        }`} />

                      <div className="flex-1 min-w-0">
                        <p className={`text-[0.9rem] font-bold truncate ${currentSessionId === chat.session_id ? "text-blue-700" : "text-gray-800"
                          }`}>
                          {chat.title || `Chat ${chat.session_id?.slice(0, 8)}`}
                        </p>
                        <p className="text-[0.78rem] text-gray-400 mt-0.5 font-normal">
                          Last message {timeAgo(chat.updated_at)}
                        </p>
                      </div>

                      <button
                        onClick={(e) => handleDeleteSession(chat.session_id, e)}
                        className="flex-shrink-0 ml-3 p-1.5 rounded opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-400 hover:bg-red-50 transition"
                        title="Delete conversation"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </li>
                  ))
                ) : (
                  <li className="text-sm text-gray-400 italic py-6">
                    {searchQuery.trim() ? "No chats match your search." : "No past conversations yet."}
                  </li>
                )}
              </ul>
            </div>
          </div>
        ) : (
          <>
            {/* Top bar (Spacer to maintain layout) */}
            <div className="w-full p-4 flex-shrink-0 min-h-[64px] flex items-center justify-between z-10 bg-white/30 backdrop-blur-sm sticky top-0 gap-2">
              <div className="flex items-center gap-2">
                {!isSidebarOpen && (
                  <button
                    onClick={() => setIsSidebarOpen(true)}
                    className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg bg-white border border-gray-200 text-blue-600 shadow-sm transition-all active:scale-95"
                    title="Open chat sidebar"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </button>
                )}
                <button
                  onClick={() => navigate(-1)}
                  className="flex items-center gap-1.5 px-3 sm:px-4 py-2 text-[0.85rem] text-white bg-blue-600 hover:bg-blue-700 rounded-full shadow border border-transparent transition-all duration-200 outline-none whitespace-nowrap"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="19" y1="12" x2="5" y2="12" />
                    <polyline points="12 19 5 12 12 5" />
                  </svg>
                  <span className="font-bold">Back</span>
                </button>
              </div>


              <Link
                to="/integrations/listing"
                className="flex items-center justify-center bg-blue-600 text-white px-5 py-2 rounded-full transition-all duration-200 font-bold shadow-sm active:scale-95 text-sm whitespace-nowrap hover:bg-blue-700"
              >
                Integrations
                <svg className="w-4 h-4 ml-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </Link>
            </div>

            {/* Scrollable Messages Area */}
            <div className={`flex-1 w-full overflow-y-auto px-4 ${messages.length === 0 ? "flex items-center justify-center pt-8" : ""}`}>
              <div className="w-full max-w-4xl mx-auto flex flex-col">

                {/* Greeting (shown when no messages) */}
                {messages.length === 0 ? (
                  <div className="text-center mb-10 pb-4 w-full">
                    <h2 className="text-3xl md:text-[2.6rem] text-gray-900 font-extrabold tracking-tight mb-8 leading-[1.1]">
                      Build integrations or analyse data<br />using <span className="bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent uppercase tracking-wider">Agent.</span>
                    </h2>

                    {/* Centered Input Box */}
                    <div className="max-w-2xl mx-auto mb-10">
                      <div className={`relative w-full transition-all duration-300 shadow-xl rounded-2xl bg-white border-2 ${isListening ? "border-blue-600 ring-2 ring-blue-100/50" : inputText.trim() ? "border-blue-600 focus-within:ring-2 focus-within:ring-blue-100/50" : "border-blue-400 focus-within:border-blue-600 focus-within:ring-2 focus-within:ring-blue-100/50"}`}>
                        <div className="px-4 py-3 flex flex-col">
                          <textarea
                            ref={centerInputRef}
                            className="w-full resize-none bg-transparent outline-none min-h-[80px] max-h-[200px] mb-2 px-1 text-gray-900 placeholder-gray-400 text-[1.05rem] font-bold"
                            placeholder="How can I help you today?"
                            value={inputText}
                            onChange={handleTextChange}
                            onKeyDown={handleKeyDown}
                            rows={1}
                            style={{ overflow: 'hidden', display: 'flex' }}
                          />

                          <div className="flex items-center justify-end mt-1 absolute bottom-3 right-4 gap-2">
                            <div className="flex items-center gap-2">

                              {/* Mic button: sound-wave idle → animated mic when recording */}
                              <button
                                onClick={handleVoiceToggle}
                                className={`relative overflow-hidden p-1.5 mr-1 transition-all rounded-xl ${isListening
                                  ? "bg-blue-100 text-blue-600 mic-listening"
                                  : "text-blue-500 hover:bg-blue-50 hover:text-blue-600"
                                  }`}
                                title={isListening ? "Stop recording" : "Use voice mode"}
                              >
                                {isListening && <span className="mic-pulse-ring" />}
                                {isListening ? (
                                  <svg className="w-5 h-5 sound-wave-active" viewBox="0 0 24 24" fill="currentColor">
                                    <rect x="4" y="9" width="1.5" height="6" rx="0.75" className="sound-wave-1" />
                                    <rect x="7" y="5" width="1.5" height="14" rx="0.75" className="sound-wave-2" />
                                    <rect x="10" y="3" width="1.5" height="18" rx="0.75" className="sound-wave-3" />
                                    <rect x="13" y="6" width="1.5" height="12" rx="0.75" className="sound-wave-4" />
                                    <rect x="16" y="4" width="1.5" height="16" rx="0.75" className="sound-wave-1" />
                                    <rect x="19" y="8" width="1.5" height="8" rx="0.75" className="sound-wave-2" />
                                  </svg>
                                ) : (
                                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                    <rect x="4" y="9" width="1.5" height="6" rx="0.75" className="sound-wave-1" />
                                    <rect x="7" y="5" width="1.5" height="14" rx="0.75" className="sound-wave-2" />
                                    <rect x="10" y="3" width="1.5" height="18" rx="0.75" className="sound-wave-3" />
                                    <rect x="13" y="6" width="1.5" height="12" rx="0.75" className="sound-wave-4" />
                                    <rect x="16" y="4" width="1.5" height="16" rx="0.75" className="sound-wave-1" />
                                    <rect x="19" y="8" width="1.5" height="8" rx="0.75" className="sound-wave-2" />
                                  </svg>
                                )}
                              </button>

                              <button
                                onClick={handleSendMessage}
                                disabled={!inputText.trim() || isLoading}
                                className={`p-1.5 rounded-full transition-transform duration-200 shadow-md ${!inputText.trim() || isLoading ? "bg-blue-300 text-white cursor-not-allowed" : "transform hover:scale-105 bg-blue-600 text-white hover:bg-blue-700"}`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                                  <line x1="22" y1="2" x2="11" y2="13"></line>
                                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Quick start examples */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto px-4">
                      {[
                        {
                          text: "When new Contact is created in HubSpot, create a Lead in Salesforce",
                          category: "CRM SYNC",
                          bgColor: "bg-orange-500/10",
                          textColor: "text-orange-400"
                        },
                        {
                          text: "When a new file is uploaded to S3, ingest it into Snowflake via SNS",
                          category: "DATA PIPELINE",
                          bgColor: "bg-purple-500/10",
                          textColor: "text-purple-400"
                        },
                        {
                          text: "Receive a webhook POST and push the payload to a REST API endpoint",
                          category: "WEBHOOK",
                          bgColor: "bg-blue-500/10",
                          textColor: "text-blue-400"
                        },
                        {
                          text: "What is the total revenue from Platinum customers?",
                          category: "BI AGENT",
                          bgColor: "bg-emerald-500/10",
                          textColor: "text-emerald-400"
                        }
                      ].map((example, i) => (
                        <button
                          key={i}
                          onClick={() => setInputText(example.text)}
                          className={`flex flex-col items-start justify-between p-4 rounded-xl transition-all duration-300 shadow-sm hover:shadow-lg border border-gray-100 hover:border-blue-200 hover:-translate-y-1 ${example.bgColor.replace('/10', '/5')} min-h-[100px] text-left`}
                        >
                          <span className="text-[0.85rem] font-bold text-gray-800 leading-snug mb-3">
                            {example.text}
                          </span>
                          <span className={`text-[0.65rem] font-black ${example.textColor} uppercase tracking-widest`}>
                            {example.category}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-5 py-4 w-full">
                    {messages.map((msg, idx) => {
                      const isLast = idx === messages.length - 1;

                      // ── User message ──────────────────────────────────────
                      if (msg.role === "user") {
                        return (
                          <div key={idx} className="flex w-full justify-end">
                            <div className="bg-blue-600 text-white rounded-[20px] rounded-tr-[6px] px-5 py-3 shadow-md max-w-[75%]">
                              {msg._transcribing ? (
                                <span className="flex items-center gap-1 h-5">
                                  <span className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                                  <span className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                                  <span className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                                </span>
                              ) : (
                                <div className="whitespace-pre-wrap text-[0.95rem] font-semibold leading-relaxed">{msg.content}</div>
                              )}
                            </div>
                          </div>
                        );
                      }
                      // ── Shared ReactMarkdown renderer (reused below) ─────────────
                      const mdComponents = {
                        p: ({ node, children, ...props }) => {
                          const hasBlockChild = React.Children.toArray(children).some(
                            child => React.isValidElement(child) && (child.type === 'pre' || child.type === 'div')
                          );
                          return hasBlockChild
                            ? <div className="mb-3 last:mb-0" {...props}>{children}</div>
                            : <p className="mb-3 last:mb-0" {...props}>{children}</p>;
                        },
                        strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
                        ul: ({ node, ...props }) => <ul className="list-disc list-outside ml-5 mb-3 last:mb-0" {...props} />,
                        ol: ({ node, ...props }) => <ol className="list-decimal list-outside ml-5 mb-3 last:mb-0" {...props} />,
                        li: ({ node, ...props }) => <li className="mb-1" {...props} />,
                        a: ({ node, href, children, ...props }) => {
                          const isInternal = href && !href.startsWith('http://') && !href.startsWith('https://') && !href.startsWith('mailto:');
                          if (isInternal) {
                            const path = href.startsWith('/') ? href : '/' + href;
                            return (
                              <a
                                href={path}
                                onClick={(e) => { e.preventDefault(); navigate(path); }}
                                className="text-[#38bdf8] hover:text-sky-300 underline underline-offset-2 font-medium cursor-pointer transition-colors"
                              >
                                {children}
                              </a>
                            );
                          }
                          return <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline underline-offset-2 font-medium transition-colors" {...props}>{children}</a>;
                        },
                        table: (props) => <PaginatedMarkdownTable {...props} />,
                        thead: ({ node, ...props }) => (
                          <thead {...props} />
                        ),
                        /* bg-blue-600 lives on the th cell — never overridden by tr */
                        th: ({ node, children, ...props }) => {
                          // Convert raw column name to a readable label
                          const KNOWN_UPPER = new Set(["id", "usd", "url", "api", "sku", "crm", "erp", "mrr", "arr"]);
                          const colLabel = (raw) =>
                            String(raw)
                              .replace(/([a-z])([A-Z])/g, "$1 $2")
                              .replace(/[_-]+/g, " ")
                              .replace(/\s+/g, " ")
                              .trim()
                              .split(" ")
                              .map((w) => {
                                const l = w.toLowerCase();
                                return KNOWN_UPPER.has(l) ? l.toUpperCase() : l.charAt(0).toUpperCase() + l.slice(1);
                              })
                              .join(" ");
                          // children may be string or array — flatten to plain text first
                          const rawText = Array.isArray(children)
                            ? children.map((c) => (typeof c === "string" ? c : "")).join("")
                            : String(children ?? "");
                          return (
                            <th
                              className="px-4 py-3 text-left text-[0.75rem] font-bold text-white tracking-wide whitespace-nowrap bg-blue-600 border-b-2 border-blue-500 border-r border-r-blue-500 last:border-r-0"
                              {...props}
                            >
                              {colLabel(rawText)}
                            </th>
                          );
                        },
                        tbody: ({ node, ...props }) => (
                          <tbody {...props} />
                        ),
                        /* tr is a group for hover — no bg set here */
                        tr: ({ node, ...props }) => (
                          <tr className="group border-b border-blue-100 last:border-b-0" {...props} />
                        ),
                        /* bg-white lives on td — group-hover changes it on row hover */
                        td: ({ node, ...props }) => (
                          <td
                            className="px-4 py-2.5 text-[0.85rem] text-black font-semibold whitespace-nowrap bg-white group-hover:bg-blue-50/60 transition-colors border-r border-blue-100 last:border-r-0"
                            {...props}
                          />
                        ),
                        pre: ({ node, ...props }) => <pre className="bg-gray-50 border border-gray-200 p-4 rounded-xl overflow-x-auto text-sm mb-4 text-gray-900 font-mono shadow-sm" {...props} />,
                        code: ({ node, inline, className, children, ...props }) => (
                          inline ?
                            <code className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded text-xs font-bold" {...props}>{children}</code> :
                            <code {...props}>{children}</code>
                        ),
                      };

                      // ── Determine active card ──────────────────────────────────────────
                      // Prefer the "current" status card (step the agent is actively asking
                      // about). Fall back to last visible card so done-only turns still work.
                      const hasCards = !!(msg.phase_cards && msg.phase_cards.length > 0);
                      const activeCard = hasCards
                        ? (msg.phase_cards.find(c => c.status === "current") ||
                           msg.phase_cards[msg.phase_cards.length - 1])
                        : null;

                      // Build the expandable content for the single active card
                      const buildActiveCardContent = () => {
                        if (!activeCard) return undefined;
                        const sections = [];
                        const cat = activeCard.category?.toUpperCase() || "";

                        // Final build card ─ show full integration summary
                        if (msg.is_done) {
                          sections.push(
                            <IntegrationSummaryCard
                              key="summary"
                              data={{
                                ...(msg.structured_data?.data || {}),
                                integration_id: msg.integration_id,
                                pipeline_id: msg.pipeline_id,
                                pipeline_type: msg.pipeline_type,
                              }}
                              isEditable={isLast && !isLoading}
                              onEdit={() => openDiagramEdit(msg)}
                            />
                          );
                        }

                        // Account selection card ─ user must pick one of multiple connections
                        if (!msg.is_done && (cat.includes("CREDENTIAL") || cat.includes("CONNECT")) && msg.needs_selection && msg.selection_options?.length > 0) {
                          sections.push(
                            <ConnectionSelectionCard
                              key="connection-selection"
                              platform={msg.selection_platform}
                              options={msg.selection_options}
                              onSelect={(label) => handleCTAAction(label, false)}
                              disabled={!isLast || isLoading}
                            />
                          );
                        }

                        // Credentials card ─ inline input form when credentials missing
                        if (!msg.is_done && (cat.includes("CREDENTIAL") || cat.includes("CONNECT")) && msg.needs_input && msg.input_fields?.length > 0) {
                          sections.push(
                            <InputFormCard
                              key="input-form"
                              platform={msg.input_platform}
                              prompt={msg.input_prompt}
                              fields={msg.input_fields}
                              onSubmit={(text) => handleCTAAction(text, false)}
                              disabled={!isLast || isLoading}
                            />
                          );
                        }

                        return sections.length > 0 ? <div className="space-y-3">{sections}</div> : undefined;
                      };

                      let activeCardOnProceed = null;
                      let activeCardProceedLabel = null;
                      const activeCat = activeCard?.category?.toUpperCase() || "";

                      if (!msg.is_done && (activeCat.includes("CREDENTIAL") || activeCat.includes("CONNECT")) && msg.structured_data?.type === "connection_status") {
                        if (msg.structured_data.data?.all_ready) {
                          activeCardOnProceed = isLast && !isLoading ? () => handleCTAAction("Connections verified, proceed to mapping") : null;
                          activeCardProceedLabel = "Continue to Mapping";
                        }
                      }

                      if (!msg.is_done && activeCat.includes("MAPPING") && msg.structured_data?.type === "field_mapping") {
                        if (!msg.structured_data.data?.approved) {
                          // For sequential intermediate steps: "Approve & Continue"; for last step: "Approve & Build"
                          const isLastMapStep = !msg.phase_cards?.some(c => c.category?.toUpperCase().includes("MAPPING") && c.status === "pending");
                          activeCardOnProceed = isLast && !isLoading ? () => handleCTAAction("Mapping looks good, proceed to build") : null;
                          activeCardProceedLabel = isLastMapStep ? "Approve & Build" : "Approve & Continue";
                        }
                      }

                      // BUILD card CTA — confirm and create the sequential integration
                      // Sequential responses use msg.step (not msg.phase) for step tracking
                      if (!msg.is_done && activeCat.includes("BUILD") && (msg.phase === "build" || msg.step === "build")) {
                        activeCardOnProceed = isLast && !isLoading ? () => handleCTAAction("proceed") : null;
                        activeCardProceedLabel = "Create Integration";
                      }

                      const activeCardContent = buildActiveCardContent();
                      // Auto-expand: open when there is inline content OR reasoning details
                      const cardAutoExpand = !!activeCardContent || !!(activeCard?.details);

                      // Show agent text below the card (never inside it)
                      const showTextBelowCard = hasCards && !!msg.content;

                      return (
                        <div key={idx} className="flex w-full justify-start gap-3">

                          {/* ── Sparkle agent icon (left of every agent turn) ── */}
                          <div className="shrink-0 mt-1">
                            <SparkleIcon className="w-5 h-5 text-blue-600" />
                          </div>

                          <div className="flex flex-col gap-2 min-w-0 flex-1">

                            {/* Thinking Block — only when there are no phase cards.
                                When cards are present, per-card reasoning details (card.details)
                                already carry scoped reasoning for each step. Showing the raw
                                LLM thinking here too would duplicate and show all-step reasoning
                                on top of every card. */}
                            {msg.thinking && !hasCards && <ThinkingBlock thinking={msg.thinking} />}

                            {hasCards ? (
                              <>
                                {msg.is_done && msg.phase_cards && msg.phase_cards.length > 1 ? (
                                  <div className="w-full flex-col">
                                    <h3 className="text-[1.1rem] font-extrabold text-gray-900 mb-1 leading-tight flex items-center gap-2">
                                      {msg.structured_data?.data?.integration_name || "Integration Setup Complete"}
                                    </h3>
                                    <p className="text-[0.85rem] text-gray-500 mb-3 font-medium">Integration created successfully</p>
                                    <PhaseTimeline
                                      cards={msg.phase_cards}
                                      structuredData={msg.structured_data}
                                      lastCardContent={
                                        <IntegrationSummaryCard
                                          data={{
                                            ...(msg.structured_data?.data || msg.structured_data || {}),
                                            integration_id: msg.integration_id || msg.pipeline_id,
                                            pipeline_id: msg.pipeline_id || msg.integration_id,
                                            pipeline_type: msg.pipeline_type,
                                            // flow_graph fallback: structured_data may have empty labels
                                            // for old sessions; msg.flow_graph always has the correct data
                                            flow_graph: msg.structured_data?.data?.flow_graph || msg.flow_graph || null,
                                          }}
                                          isEditable={isLast && !isLoading}
                                          onEdit={() => openDiagramEdit(msg)}
                                        />
                                      }
                                    />
                                  </div>
                                ) : (
                                  <>
                                    <div className="w-full max-w-2xl my-1">
                                      {/* ── Single active card for this turn ── */}
                                      <PhaseCard
                                        stepNumber={activeCard.step_number}
                                        category={activeCard.category}
                                        title={activeCard.title}
                                        icon={activeCard.icon}
                                        tags={[]}
                                        status={activeCard.status || "current"}
                                        expandableContent={activeCardContent}
                                        defaultExpanded={cardAutoExpand}
                                        onProceed={activeCardOnProceed}
                                        proceedLabel={activeCardProceedLabel}
                                        details={activeCard.details || null}
                                      />
                                    </div>

                                    {/* ── Agent text below card ONLY when user needs to act ── */}
                                    {showTextBelowCard && (
                                      <CollapsibleText autoExpand={isLast && (msg.needs_input || (!msg.is_done && !activeCardOnProceed))}>
                                        <div className="text-[0.93rem] text-black leading-relaxed font-normal markdown-body">
                                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                                            {msg.content}
                                          </ReactMarkdown>
                                        </div>
                                      </CollapsibleText>
                                    )}
                                  </>
                                )}
                              </>
                            ) : (
                              // ── Plain text / no-card message ──
                              <div className="flex flex-col gap-2">
                                {/* ── Model Reasoning trace (expanded by default for BI agent) ── */}
                                {msg.reasoning_steps && msg.reasoning_steps.length > 0 && (
                                  <div className="mb-1">
                                    <ModelReasoningCard steps={msg.reasoning_steps} defaultExpanded={msg.agent_used === "analyst"} />
                                  </div>
                                )}

                                {msg.content && !(msg.update_options && msg.update_options.length > 0) && (
                                  <div className="text-[1.0rem] tracking-[0.015em] leading-[1.7] break-words markdown-body text-black">
                                    <ReactMarkdown
                                      remarkPlugins={[remarkGfm]}
                                      components={{
                                        ...mdComponents,
                                        table: msg.structured_data?.structured_data_type === "data_table" ? () => null : mdComponents.table
                                      }}
                                    >
                                      {msg.content}
                                    </ReactMarkdown>
                                  </div>
                                )}

                                {/* Standalone account selection (connect step, no phase cards) */}
                                {msg.needs_selection && msg.selection_options?.length > 0 && (
                                  <ConnectionSelectionCard
                                    platform={msg.selection_platform}
                                    options={msg.selection_options}
                                    onSelect={(label) => handleCTAAction(label, false)}
                                    disabled={!isLast || isLoading}
                                  />
                                )}

                                {/* Standalone input form (connect step, no phase cards) */}
                                {msg.needs_input && msg.input_fields?.length > 0 && (
                                  <InputFormCard
                                    platform={msg.input_platform}
                                    prompt={msg.input_prompt}
                                    fields={msg.input_fields}
                                    onSubmit={(text) => handleCTAAction(text, false)}
                                    disabled={!isLast || isLoading}
                                  />
                                )}

                                {/* ── Update picker: integration / pipeline select cards ── */}
                                {msg.update_options?.length > 0 && (
                                  <UpdateOptionsPicker
                                    options={msg.update_options}
                                    onSelect={(name) => handleCTAAction(name, false)}
                                    disabled={!isLast || isLoading}
                                  />
                                )}

                                {/* Standalone connection status */}
                                {msg.structured_data?.type === "connection_status" && (
                                  <ConnectionStatusCard
                                    data={msg.structured_data.data}
                                    onProceed={isLast && !isLoading ? () => handleCTAAction("Connections verified, proceed to mapping") : null}
                                  />
                                )}

                                {/* Standalone mapping */}
                                {msg.structured_data?.type === "field_mapping" && (
                                  <MappingCard
                                    data={msg.structured_data.data}
                                    onProceed={isLast && !isLoading ? () => handleCTAAction("Mapping looks good, proceed to build") : null}
                                  />
                                )}

                                {/* Standalone summary (final step) */}
                                {msg.is_done && !msg.structured_data?.structured_data_type && (
                                  <IntegrationSummaryCard
                                    data={{
                                      ...(msg.structured_data?.data || msg.structured_data || {}),
                                      integration_id: msg.integration_id || msg.pipeline_id,
                                      pipeline_id: msg.pipeline_id || msg.integration_id,
                                      pipeline_type: msg.pipeline_type,
                                    }}
                                    isEditable={isLast && !isLoading}
                                    onEdit={() => openDiagramEdit(msg)}
                                  />
                                )}

                                {/* ── Agent badge (shows which agent handled this turn) ── */}


                                {/* ── Clarification chips (router couldn't decide intent) ── */}
                                {msg.needs_clarification && msg.clarification_options?.length > 0 && isLast && !isLoading && (
                                  <ClarificationChips
                                    question={msg.content}
                                    options={msg.clarification_options}
                                    onSelect={handleClarificationSelect}
                                  />
                                )}

                                {/* ── BI Agent — text-only mode ────────────────────── */}
                                {/* When agent_used === "analyst", show plain text only.   */}
                                {/* For the query approval step, show minimal Run/Cancel   */}
                                {/* buttons inline so the workflow still functions.        */}
                                {msg.agent_used === "analyst" && msg.needs_approval && isLast && !isLoading && (
                                  <div className="mt-3 flex items-center gap-3">
                                    <button
                                      onClick={handleApproveQuery}
                                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[0.85rem] font-bold bg-blue-600 text-white hover:bg-blue-700 active:scale-95 transition-all shadow-sm"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                                      </svg>
                                      Run Query
                                    </button>
                                    <button
                                      onClick={handleCancelQuery}
                                      className="px-4 py-2 rounded-xl text-[0.85rem] font-bold border border-gray-200 text-gray-600 hover:border-red-300 hover:text-red-500 hover:bg-red-50 active:scale-95 transition-all"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                )}

                                {/* ── BI Agent rich cards ── */}
                                <>
                                  {msg.structured_data?.structured_data_type === "query_preview" && (
                                    <QueryPreviewCard
                                      data={msg.structured_data}
                                      onApprove={isLast && !isLoading ? handleApproveQuery : null}
                                      onCancel={isLast && !isLoading ? handleCancelQuery : null}
                                      disabled={!isLast || isLoading}
                                    />
                                  )}
                                  {msg.structured_data?.structured_data_type === "query_plan" && (
                                    <QueryPlanCard data={msg.structured_data} />
                                  )}
                                  {msg.structured_data?.structured_data_type === "metric_card" && (
                                    <MetricCard data={msg.structured_data} />
                                  )}
                                  {msg.structured_data?.structured_data_type === "data_table" && (
                                    <DataTableCard data={msg.structured_data} />
                                  )}
                                </>

                              </div>
                            )}

                          </div>
                        </div>
                      );
                    })}
                    {isLoading && (
                      agentUsedLast === "builder"
                        ? <ThinkingLoader phase={currentPhase} />
                        : <BIThinkingLoader />
                    )}
                    {/* Scroll anchor */}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>
            </div>

            {/* Input Box Container - Pinned at bottom */}
            {messages.length > 0 && (
              <div className="w-full flex-shrink-0 px-4 pb-8 pt-2 bg-transparent">
                <div className="w-full max-w-4xl mx-auto">
                  <div className={`relative w-full shadow-2xl rounded-full bg-white border-2 transition-all ${isListening ? "border-blue-600 ring-4 ring-blue-50" : inputText.trim() ? "border-blue-600 ring-4 ring-blue-50" : "border-blue-400 focus-within:border-blue-600 focus-within:ring-4 focus-within:ring-blue-50"}`}>
                    <div className="px-4 py-2 flex items-center">
                      <textarea
                        ref={bottomInputRef}
                        className="w-full resize-none bg-transparent outline-none min-h-[24px] max-h-[150px] text-gray-900 placeholder-gray-400 text-[1rem] flex-1 py-2 font-bold"
                        placeholder={
                          isListening
                            ? "Listening..."
                            : currentPhase === "discover"
                              ? "I want to..."
                              : currentPhase === "connect"
                                ? "Provide your credentials or type your response..."
                                : "Type your response..."
                        }
                        value={inputText}
                        onChange={handleTextChange}
                        onKeyDown={handleKeyDown}
                        rows={1}
                        readOnly={isListening}
                        style={{ overflow: 'hidden', display: 'flex' }}
                      />

                      <div className="flex items-center gap-2 ml-2 shrink-0">
                        {/* Mic button: sound-wave idle → animated mic when recording */}
                        <button
                          onClick={handleVoiceToggle}
                          className={`relative overflow-hidden p-1.5 mr-1 transition-all rounded-xl ${isListening
                            ? "bg-blue-100 text-blue-600 mic-listening"
                            : "text-blue-500 hover:bg-blue-50 hover:text-blue-600"
                            }`}
                          title={isListening ? "Stop recording" : "Use voice mode"}
                        >
                          {isListening && <span className="mic-pulse-ring" />}
                          {isListening ? (
                            <svg className="w-5 h-5 sound-wave-active" viewBox="0 0 24 24" fill="currentColor">
                              <rect x="4" y="9" width="1.5" height="6" rx="0.75" className="sound-wave-1" />
                              <rect x="7" y="5" width="1.5" height="14" rx="0.75" className="sound-wave-2" />
                              <rect x="10" y="3" width="1.5" height="18" rx="0.75" className="sound-wave-3" />
                              <rect x="13" y="6" width="1.5" height="12" rx="0.75" className="sound-wave-4" />
                              <rect x="16" y="4" width="1.5" height="16" rx="0.75" className="sound-wave-1" />
                              <rect x="19" y="8" width="1.5" height="8" rx="0.75" className="sound-wave-2" />
                            </svg>
                          ) : (
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                              <rect x="4" y="9" width="1.5" height="6" rx="0.75" className="sound-wave-1" />
                              <rect x="7" y="5" width="1.5" height="14" rx="0.75" className="sound-wave-2" />
                              <rect x="10" y="3" width="1.5" height="18" rx="0.75" className="sound-wave-3" />
                              <rect x="13" y="6" width="1.5" height="12" rx="0.75" className="sound-wave-4" />
                              <rect x="16" y="4" width="1.5" height="16" rx="0.75" className="sound-wave-1" />
                              <rect x="19" y="8" width="1.5" height="8" rx="0.75" className="sound-wave-2" />
                            </svg>
                          )}
                        </button>

                        <button
                          onClick={handleSendMessage}
                          disabled={!inputText.trim() || isLoading}
                          className={`p-1.5 rounded-full transition-transform duration-200 shadow-md ${!inputText.trim() || isLoading ? "bg-blue-300 text-white cursor-not-allowed" : "transform hover:scale-105 bg-blue-600 text-white hover:bg-blue-700"}`}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                            <line x1="22" y1="2" x2="11" y2="13"></line>
                            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {/* ── DiagramEditModal — renders as portal above everything ── */}
      <DiagramEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onApply={handleDiagramEdit}
        currentFlow={editModalData.currentFlow}
        currentMappings={editModalData.currentMappings}
        currentBranchMappings={editModalData.currentBranchMappings}
        flowGraph={editModalData.flowGraph || null}
      />
    </div>
  );
};

export default AgentChat;
